/**
 * Centralized utility for prep item calculations and data transformations
 * This module provides consistent logic across PrepGuide, PrepGuideSummaryModal, printPDF, and index.tsx
 */
import moment from 'moment'

interface IOnHandDetail {
  prepBatchType: string
  standardUnitsPerBatch: number
}

interface IOnHandUnit {
  type: string
  standardUnitPerUnit: number
}

export interface IPrepItemBase {
  prepItemIdCode: string
  prepItemName: string
  prepItemUnit: string
  unitAmountNeeded: number
  totalUnitAmountUntilNextPrep?: number // Total requirements for all days until next prep
  prepBatchType: string
  standardUnitsPerBatch: number
  minimumUnit: number
  onHandUnit?: IOnHandUnit | null
  prepOnHandDetails?: IOnHandDetail[]
  restDays?: number // Extra days to include in prep calculation
  prepFloorPercent?: number // Per-item minimum prep increase (e.g. 15 for brownies); 0/undefined for items with no floor
  // Pre-calculated values from historical records
  storedOnHand?: number
  storedNeeded?: number
  storedPrep?: number
}

interface IHistoricalOnHandDetail {
  batchType: string
  quantity: number
  standardUnitsPerBatch: number
}

interface IHistoricalPrepItem {
  prepItemIdCode: string
  prepItemName: string
  prepItemUnit: string
  prepBatchType: string
  standardUnitsPerBatch: number
  unitAmountNeeded: number
  adjustedUnitAmountNeeded: number
  totalUnitAmountUntilNextPrep?: number
  minimumUnit: number
  onHandDetails?: IHistoricalOnHandDetail[]
  onHandUnit?: IOnHandUnit | null
  onHand: number
  needed: number
  prep: number
}

interface IPrepItemCalculations {
  onHand: number
  displayOnHand: number
  adjustedUnitAmountNeeded: number
  needed: number
  prep: number
}

/**
 * Calculate the total on-hand amount in standard units
 */
const calculateOnHand = (
  item: IPrepItemBase,
  onHandValues: Record<string, Record<string, number>>,
): number => {
  if (!item.prepOnHandDetails || item.prepOnHandDetails.length === 0) {
    return 0
  }

  return item.prepOnHandDetails.reduce((total, detail) => {
    const quantity =
      onHandValues[item.prepItemName]?.[detail.prepBatchType] || 0
    return total + quantity * detail.standardUnitsPerBatch
  }, 0)
}

/**
 * Convert on-hand amount to display units based on onHandUnit configuration
 */
const getDisplayOnHand = (
  onHand: number,
  onHandUnit?: IOnHandUnit | null,
): number => {
  return onHandUnit?.standardUnitPerUnit
    ? onHand / onHandUnit.standardUnitPerUnit
    : onHand
}

/**
 * Get the display unit label for on-hand
 */
export const getOnHandUnitLabel = (
  item: IPrepItemBase | IHistoricalPrepItem,
): string => {
  return item.onHandUnit?.type || item.prepItemUnit
}

const BUCKET_ITEM_ID_CODES = ['carrots', 'celery']

export const isBucketItem = (prepItemIdCode: string): boolean =>
  BUCKET_ITEM_ID_CODES.includes(prepItemIdCode)

// why: carrots & celery display Needed/Prep in buckets, not batches. Convert the
// calculated batch values by the ratio of prep batch size to on-hand bucket
// size. Returns 1 (no-op) for every other item. Shared by PrepGuide,
// PrepGuideSummaryModal, and printPDF so the conversion stays in sync.
export const getBucketFactor = (item: IPrepItemBase): number =>
  isBucketItem(item.prepItemIdCode) && item.onHandUnit?.standardUnitPerUnit
    ? item.standardUnitsPerBatch / item.onHandUnit.standardUnitPerUnit
    : 1

/**
 * Calculate needed amount, rounded up at the granularity of the display unit
 * (0.1 bucket for carrots/celery via displayFactor, 0.1 batch otherwise).
 */
const calculateNeeded = (
  adjustedUnitAmountNeeded: number,
  standardUnitsPerBatch: number,
  displayFactor: number,
): number => {
  const neededAmount =
    (adjustedUnitAmountNeeded / standardUnitsPerBatch) * displayFactor
  return Math.ceil(neededAmount * 10) / 10
}

/**
 * Calculate prep amount, rounded up at the granularity of the display unit.
 * Uses the total unit amount needed until next prep (sum of all days)
 * Can be negative when on-hand exceeds needed amount
 */
const calculatePrep = (
  adjustedTotalUnitAmountUntilNextPrep: number,
  onHand: number,
  standardUnitsPerBatch: number,
  minimumUnit: number,
  displayFactor: number,
): number => {
  if (minimumUnit === 0) {
    // why: round in the same unit carrots/celery are displayed in (bucket),
    // not in the underlying batch unit — otherwise a value that already
    // landed on a 0.1-batch boundary can round to a much coarser display
    // step (e.g. 0.36 bucket rounds up to 0.4 instead of the true 0.4→ceil
    // step of 0.1 bucket). Non-bucket items pass displayFactor 1, no change.
    const prepCalculation =
      ((adjustedTotalUnitAmountUntilNextPrep - onHand) /
        standardUnitsPerBatch) *
      displayFactor
    return Math.ceil(prepCalculation * 10) / 10
  } else {
    const prepCalculation =
      (adjustedTotalUnitAmountUntilNextPrep - onHand) / standardUnitsPerBatch
    return Math.ceil(prepCalculation / minimumUnit) * minimumUnit
  }
}

/**
 * Perform all calculations for a prep item
 * If stored values exist (from historical records), use them directly
 */
export const calculatePrepItemValues = (
  item: IPrepItemBase,
  adjustment: number,
  onHandValues: Record<string, Record<string, number>>,
): IPrepItemCalculations => {
  // If stored values exist (historical record), use them directly
  if (
    item.storedOnHand !== undefined &&
    item.storedNeeded !== undefined &&
    item.storedPrep !== undefined
  ) {
    const displayOnHand = getDisplayOnHand(item.storedOnHand, item.onHandUnit)
    return {
      onHand: item.storedOnHand,
      displayOnHand,
      adjustedUnitAmountNeeded: item.unitAmountNeeded * (1 + adjustment / 100),
      needed: item.storedNeeded,
      prep: item.storedPrep,
    }
  }

  const onHand = calculateOnHand(item, onHandValues)
  const displayOnHand = getDisplayOnHand(onHand, item.onHandUnit)
  const displayFactor = getBucketFactor(item)
  const adjustedUnitAmountNeeded =
    item.unitAmountNeeded * (1 + adjustment / 100)
  const needed = calculateNeeded(
    adjustedUnitAmountNeeded,
    item.standardUnitsPerBatch,
    displayFactor,
  )

  // For prep calculation, use totalUnitAmountUntilNextPrep if available
  // Otherwise fall back to multiplying today's amount by days
  const totalUnitAmount =
    item.totalUnitAmountUntilNextPrep ?? item.unitAmountNeeded
  // why: the per-item prep floor is a guaranteed MINIMUM increase for
  // brownies/blue-cheese/carrots, on the scheduled list only. Prep uses the larger of the store's
  // adjustment and the floor (not the two multiplied), so lowering the
  // adjustment can never drop these items below their floor and the floor can
  // never inflate prep on top of the adjustment (ENG-4900).
  // why: an item with no floor takes the adjustment unchanged, including a
  // negative one. Guarding on the floor rather than max()-ing against 0 is what
  // lets Prep fall with Needed (ENG-6414).
  const prepPercent = item.prepFloorPercent
    ? Math.max(item.prepFloorPercent, adjustment)
    : adjustment
  const adjustedTotalUnitAmount = totalUnitAmount * (1 + prepPercent / 100)

  const prep = calculatePrep(
    adjustedTotalUnitAmount,
    onHand,
    item.standardUnitsPerBatch,
    item.minimumUnit,
    displayFactor,
  )

  return {
    onHand,
    displayOnHand,
    adjustedUnitAmountNeeded,
    needed,
    prep,
  }
}

/**
 * Transform historical record items to the standard format
 * Preserves the stored calculated values so they display exactly as submitted
 */
export const transformHistoricalItem = (
  item: IHistoricalPrepItem,
): IPrepItemBase => {
  return {
    prepItemIdCode: item.prepItemIdCode,
    prepItemName: item.prepItemName,
    prepItemUnit: item.prepItemUnit,
    unitAmountNeeded: item.unitAmountNeeded,
    totalUnitAmountUntilNextPrep: item.totalUnitAmountUntilNextPrep,
    prepBatchType: item.prepBatchType,
    standardUnitsPerBatch: item.standardUnitsPerBatch,
    minimumUnit: item.minimumUnit,
    onHandUnit: item.onHandUnit,
    prepOnHandDetails: item.onHandDetails?.map((detail) => ({
      prepBatchType: detail.batchType,
      standardUnitsPerBatch: detail.standardUnitsPerBatch,
    })),
    // Pass stored values directly to avoid recalculation
    storedOnHand: item.onHand,
    storedNeeded: item.needed,
    storedPrep: item.prep,
  }
}

/**
 * Format currency for display
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Format prep value for prep items (scheduled today)
 * Green cell with parentheses when value <= 0: have enough, showing extra amount
 * Green cell, no parentheses when value > 0: need to prep
 */
export const formatPrepItemValue = (value: number): string => {
  if (value <= 0) {
    return `(${Math.abs(value).toFixed(1)})`
  }
  return value.toFixed(1)
}

/**
 * Format prep value for non-prep items (not scheduled today)
 * Red cell with parentheses when value > 0: don't have enough, need to prep
 * Gray/white cell, no parentheses when value <= 0: have enough on hand
 */
export const formatNonPrepItemValue = (value: number): string => {
  if (value > 0) {
    return `(${value.toFixed(1)})`
  }
  // Show absolute value without negative sign
  return Math.abs(value).toFixed(1)
}

/**
 * Get background color for prep value (prep items - scheduled today)
 */
export const getPrepBackgroundColor = (_: number): string => {
  return '#e8f5e9' // Always green for prep items
}

/**
 * Get background color for prep value (non-prep items - not scheduled today)
 */
export const getNonPrepBackgroundColor = (value: number): string => {
  if (value > 0) {
    return '#ffebee' // Red for items that need prep even though not scheduled
  }
  return '#f5f5f5' // Light gray for items with enough on hand
}

/**
 * Get text color for prep value (prep items - scheduled today)
 */
export const getPrepTextColor = (_: number): string => {
  return '#4caf50' // Always green text for prep items
}

/**
 * Get text color for prep value (non-prep items - not scheduled today)
 */
export const getNonPrepTextColor = (value: number): string => {
  if (value > 0) {
    return '#d32f2f' // Red text for items that need prep
  }
  return '#999999' // Gray text for items with enough on hand
}

/**
 * Calculate the number of days until the next prep day for an item
 * @param currentDate - The current date
 * @param weekdayConfig - Object with boolean flags for each weekday (monday, tuesday, etc.)
 * @returns Number of days until next prep (minimum 1, maximum 7)
 */
export const calculateDaysUntilNextPrep = (
  currentDate: moment.Moment,
  weekdayConfig: {
    monday?: boolean
    tuesday?: boolean
    wednesday?: boolean
    thursday?: boolean
    friday?: boolean
    saturday?: boolean
    sunday?: boolean
  },
): number => {
  const weekdays = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]

  // Start checking from tomorrow
  for (let i = 1; i <= 7; i++) {
    const nextDate = currentDate.clone().add(i, 'days')
    const dayName = weekdays[nextDate.day()]
    if (weekdayConfig[dayName as keyof typeof weekdayConfig]) {
      return i
    }
  }

  // If no next prep day found in the next week, default to 1
  return 1
}
